import { isPlatformServer } from '@angular/common';
import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { asyncScheduler, observeOn, of, tap } from 'rxjs';

interface CacheEntry {
  data: any;
  timestamp: number;
}

// In-memory cache (browser only — process-global Map must not grow during SSR)
const cache = new Map<string, CacheEntry>();

const CACHE_TTL_DEFAULT = 5 * 60 * 1000;   // 5 min
const CACHE_TTL_LONG = 15 * 60 * 1000;       // 15 min for categories / product lists
const MAX_CACHE_ENTRIES = 80;

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip module-level cache during SSR (shared across requests → heap growth)
  if (isPlatformServer(inject(PLATFORM_ID))) {
    return next(req);
  }

  // Only cache GET requests
  if (req.method !== 'GET') {
    return next(req);
  }

  // Skip caching if noCache param is set (HttpClient params or raw query string — HttpService often embeds query in URL only)
  const fullUrl = req.urlWithParams.toLowerCase();
  if (req.params.get('noCache') === 'true' || fullUrl.includes('nocache=true')) {
    return next(req);
  }

  // Skip caching for auth, admin, and geo-IP lookups (must reflect the current visitor)
  const url = req.url.toLowerCase();
  if (
    url.includes('ipwho.is') ||
    url.includes('ipapi.co') ||
    url.includes('ipinfo.io') ||
    url.includes('geojs.io') ||
    url.includes('/geo/location') ||
    url.includes('/auth/') ||
    url.includes('/login') ||
    url.includes('/signup') ||
    url.includes('/admin/') ||
    url.includes('seller-drug-program') ||
    // Public seller profile — must not use sync `of()` cache (breaks change detection on client nav)
    /\/seller\/[^/?]+/i.test(url) ||
    url.includes('/seller/brand-site/') ||
    url.includes('/ai-websites/') ||
    url.includes('/streams') ||
    url.includes('/live-streams') ||
    url.includes('/auctions/') ||
    url.includes('/browsing-history') ||
    // Digital product passports — must not use sync cache (stuck "Loading…" with OnPush + withFetch)
    url.includes('/passports')
  ) {
    return next(req);
  }

  const cachedResponse = getCachedResponse(req, getCacheTtl(url));
  if (cachedResponse) {
    // Defer to next microtask so Zone/CD runs after subscribe (fixes stuck loading UIs)
    return of(cachedResponse).pipe(observeOn(asyncScheduler));
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.status === 200) {
        cacheResponse(req, event, getCacheTtl(url));
      }
    })
  );
};

function getCacheTtl(url: string): number {
  const u = url.toLowerCase();
  // Use path `/products` — not substring `product` (otherwise `seller-drug-program` wrongly gets a long TTL)
  if (
    u.includes('categor') ||
    u.includes('/products/slug/') ||
    (u.includes('/products/') && !u.includes('product-detail') && !u.includes('/products?'))
  ) {
    return CACHE_TTL_LONG;
  }
  return CACHE_TTL_DEFAULT;
}

function getCachedResponse(req: HttpRequest<any>, ttl: number): HttpResponse<any> | null {
  const key = getCacheKey(req);
  const entry = cache.get(key);

  if (!entry) {
    return null;
  }

  const now = Date.now();
  if (now - entry.timestamp > ttl) {
    cache.delete(key);
    return null;
  }

  // Return cached response
  return new HttpResponse({
    body: entry.data,
    status: 200,
    statusText: 'OK (Cached)',
    headers: req.headers,
    url: req.url
  });
}

function cacheResponse(req: HttpRequest<any>, response: HttpResponse<any>, ttl: number): void {
  const key = getCacheKey(req);

  if (response.body && response.status === 200) {
    cache.set(key, {
      data: response.body,
      timestamp: Date.now()
    });
    if (cache.size > MAX_CACHE_ENTRIES) {
      cleanupCache(ttl);
    }
  }
}

function getCacheKey(req: HttpRequest<any>): string {
  // Create a unique key from URL and query params
  const url = req.urlWithParams;
  return `cache_${url}`;
}

function cleanupCache(ttl: number = CACHE_TTL_DEFAULT): void {
  const now = Date.now();
  const keysToDelete: string[] = [];
  cache.forEach((entry, key) => {
    if (now - entry.timestamp > ttl) keysToDelete.push(key);
  });
  keysToDelete.forEach(key => cache.delete(key));
  if (cache.size > MAX_CACHE_ENTRIES) {
    const sorted = [...cache.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp);
    sorted.slice(0, sorted.length - MAX_CACHE_ENTRIES).forEach(([k]) => cache.delete(k));
  }
}

